import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { TabActions, TabCard } from '@/components/layout/tab-card'
import { SelectOption } from '@/components/ui/select'
import { DirtyFieldChange } from '@/hooks'
import { selectBuildings } from '@/reducers/buildingSelectorReducer'
import { Building } from '@/reducers/types'

import { FormChangeFields, getFormChanges } from '../../../../shared/form-changes'
import { PlaceCountry } from '../../../place-form-modal/place-form-modal.constants'
import { IS_CONVENIENCE_FEE_ENABLED } from '../../../places.gates'
import { PLACE_TIMEZONES } from '../../../places.timezones'
import { getPlaceTags } from '../../../places.utils'
import { usePlaceSave } from '../../use-place-save'
import { GeneralEditFields } from './general-edit-fields'
import {
  getPlaceGeneralDefaults,
  makePlaceGeneralSchema,
  PLACE_GENERAL_FLAG_KEYS,
  PLACE_GENERAL_SWITCH_KEYS,
  PLACE_GENERAL_TEXT_KEYS,
  PlaceGeneralFormData,
} from './general-tab.constants'
import { getPlaceGeneralPayload } from './general-tab.utils'
import { GeneralViewFields } from './general-view-fields'

interface GeneralTabProps {
  building: Building
  isAdmin: boolean
  onDirtyChange: (isDirty: boolean, getChanges?: () => DirtyFieldChange[]) => void
}

const TEXT_FIELD_LABELS = {
  address: 'organisation:places.building.fields.address.label',
  appName: 'organisation:places.building.fields.appName.label',
  city: 'organisation:places.building.fields.city.label',
  email: 'organisation:places.building.fields.email.label',
  emergencyPhone: 'organisation:places.building.fields.emergencyPhone.label',
  googlePlayLink: 'organisation:places.building.fields.googlePlayLink.label',
  hubspotDealId: 'organisation:places.building.fields.hubspotDealId.label',
  iosAppStoreLink: 'organisation:places.building.fields.iosAppStoreLink.label',
  link: 'organisation:places.building.fields.link.label',
  maintenanceRequestLink: 'organisation:places.building.fields.maintenanceRequestLink.label',
  name: 'organisation:places.building.fields.name.label',
  payRentLink: 'organisation:places.building.fields.payRentLink.label',
  postalCode: 'organisation:places.building.fields.postalCode.label',
  state: 'organisation:places.building.fields.state.label',
} as const

export const GeneralTab = ({ building, isAdmin, onDirtyChange }: GeneralTabProps) => {
  const { t } = useTranslation(['organisation'])
  const buildings = useSelector(selectBuildings)
  const { isSaving, save } = usePlaceSave(building)
  const [isEditing, setIsEditing] = useState(false)

  const schema = useMemo(
    () => makePlaceGeneralSchema(t, building.country ?? PlaceCountry.US),
    [t, building.country],
  )
  const serverValues = useMemo(() => getPlaceGeneralDefaults(building), [building])

  const form = useForm<PlaceGeneralFormData>({
    defaultValues: serverValues,
    mode: 'onTouched',
    // Note: a background update of the building must not wipe what the user is editing
    resetOptions: { keepDirtyValues: true },
    resolver: zodResolver(schema),
    values: serverValues,
  })

  const { isDirty } = form.formState

  const tagOptions = useMemo(
    () => getPlaceTags(buildings).map((tag) => ({ label: tag, value: tag })),
    [buildings],
  )

  const timezoneOptions = useMemo<SelectOption<string>[]>(
    () => [
      { label: t('organisation:places.building.timezone.options.pt'), value: PLACE_TIMEZONES.PT },
      { label: t('organisation:places.building.timezone.options.mt'), value: PLACE_TIMEZONES.MT },
      { label: t('organisation:places.building.timezone.options.az'), value: PLACE_TIMEZONES.AZ },
      { label: t('organisation:places.building.timezone.options.ct'), value: PLACE_TIMEZONES.CT },
      { label: t('organisation:places.building.timezone.options.et'), value: PLACE_TIMEZONES.ET },
    ],
    [t],
  )

  const changeFields = useMemo<FormChangeFields<PlaceGeneralFormData>>(() => {
    const fields: FormChangeFields<PlaceGeneralFormData> = {
      buildingResidence: {
        label: t('organisation:places.building.fields.buildingResidence.label'),
      },
      code: { label: t('organisation:places.building.fields.code.label') },
      image: { image: true, label: t('organisation:places.building.buildingPhoto') },
      logo: { image: true, label: t('organisation:places.building.logotype') },
      phone: { label: t('organisation:places.building.fields.phone.label') },
      tags: {
        items: (value) => value as string[],
        label: t('organisation:places.building.fields.tags.label'),
      },
      timezone: {
        format: (value) =>
          timezoneOptions.find((option) => option.value === value)?.label ?? String(value),
        label: t('organisation:places.building.timezone.label'),
      },
    }

    PLACE_GENERAL_TEXT_KEYS.forEach((key) => {
      fields[key] = { label: t(TEXT_FIELD_LABELS[key]) }
    })
    PLACE_GENERAL_FLAG_KEYS.forEach((key) => {
      fields[key] = {
        format: (value) =>
          value ? t('organisation:places.save.on') : t('organisation:places.save.off'),
        label: t(`organisation:places.building.toggles.${key}`),
      }
    })
    if (IS_CONVENIENCE_FEE_ENABLED) {
      fields.residentConvenienceFeeRate = {
        label: t('organisation:places.building.fields.residentConvenienceFeeRate.label'),
      }
      PLACE_GENERAL_SWITCH_KEYS.forEach((key) => {
        fields[key] = {
          format: (value) =>
            value ? t('organisation:places.save.on') : t('organisation:places.save.off'),
          label: t(`organisation:places.building.fields.${key}.label`),
        }
      })
    }

    return fields
  }, [t, timezoneOptions])

  const getChanges = useCallback(
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    [changeFields, form],
  )

  useEffect(() => {
    const isDirtyForm = isEditing && isDirty
    onDirtyChange(isDirtyForm, isDirtyForm ? getChanges : undefined)
  }, [isEditing, isDirty, getChanges, onDirtyChange])

  const handleCancel = () => {
    form.reset(serverValues)
    setIsEditing(false)
  }

  const handleSave = form.handleSubmit(async (values) => {
    const { nextFlags, payload } = getPlaceGeneralPayload({ isAdmin, server: building, values })
    const result = await save({ nextFlags, payload })

    if (result.ok) {
      form.reset(getPlaceGeneralDefaults(result.building))
      setIsEditing(false)
    } else if ('error' in result && result.error.kind === 'codeExists') {
      form.setError('code', { message: t('organisation:places.building.errors.codeExists') })
    }
  })

  const countryLabel =
    building.country === PlaceCountry.CA
      ? t('organisation:places.create.country.ca')
      : t('organisation:places.create.country.us')
  const timezoneLabel =
    timezoneOptions.find((option) => option.value === serverValues.timezone)?.label ?? ''

  return (
    <FormProvider {...form}>
      <TabCard
        actions={
          <TabActions
            isDirty={isDirty}
            isEditing={isEditing}
            isSaving={isSaving}
            onCancel={handleCancel}
            onEdit={() => setIsEditing(true)}
            onSave={handleSave}
          />
        }
        title={t('organisation:places.general.title')}
      >
        {isEditing ? (
          <GeneralEditFields
            building={building}
            countryLabel={countryLabel}
            isAdmin={isAdmin}
            tagOptions={tagOptions}
            timezoneOptions={timezoneOptions}
          />
        ) : (
          <GeneralViewFields
            building={building}
            countryLabel={countryLabel}
            isAdmin={isAdmin}
            timezoneLabel={timezoneLabel}
          />
        )}
      </TabCard>
    </FormProvider>
  )
}
